import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import { AppointmentFeedbackStatus } from 'src/types/dto/feedbackCode.dto';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TimezoneDTO } from 'src/types/dto/timezone.dto';
import { InspectorDTO, UnavailabilityPeriodDTO } from 'src/types/dto/inspector.dto';

import {
  durationDaysToSeconds,
  durationSecondsToAllocationDays,
  MIN_DURATION_DAYS,
  reconcileDisplayDurationsToTotal,
  roundDuration,
} from './duration';

dayjs.extend(utc);

/** Prefer durationSeconds (allocation source of truth); fall back to durationDays at 5dp. */
export const appointmentDisplayDurationDays = (appointment: AppointmentDTO): number => {
  if (
    appointment.durationSeconds != null &&
    Number.isFinite(Number(appointment.durationSeconds))
  ) {
    return durationSecondsToAllocationDays(Number(appointment.durationSeconds));
  }
  if (appointment.durationDays != null && Number.isFinite(Number(appointment.durationDays))) {
    return roundDuration(Number(appointment.durationDays));
  }
  return 0;
};

const isCountingWorkAppointment = (appointment: AppointmentDTO): boolean => {
  if (appointment.type === 'blocker') {
    return false;
  }
  // Non-counting roles must not absorb the activity-duration remainder.
  return Boolean(appointment.projectRole?.countsTowardAudit ?? true);
};

const DISPLAY_DURATION_RECONCILE_TOLERANCE_DAYS = 0.0001;

const resolveSecondsEncodedActivityDays = (
  counting: AppointmentDTO[],
): number | null => {
  const hasAllSeconds = counting.every(
    (appointment) =>
      appointment.durationSeconds != null &&
      Number.isFinite(Number(appointment.durationSeconds)),
  );
  if (!hasAllSeconds) {
    return null;
  }

  const secondsTotal = counting.reduce(
    (sum, appointment) => sum + Math.round(Number(appointment.durationSeconds)),
    0,
  );
  if (secondsTotal <= 0) {
    return null;
  }

  const fromSeconds = durationSecondsToAllocationDays(secondsTotal);
  const nearestInt = Math.round(fromSeconds);
  const snapped =
    nearestInt > 0 &&
    roundDuration(Math.abs(nearestInt - fromSeconds)) <= DISPLAY_DURATION_RECONCILE_TOLERANCE_DAYS
      ? nearestInt
      : fromSeconds;

  if (
    secondsTotal === durationDaysToSeconds(snapped) ||
    durationSecondsToAllocationDays(secondsTotal) === snapped
  ) {
    return snapped;
  }

  return null;
};

/**
 * Per-appointment display days for tables/cards.
 *
 * Always derives each row from durationSeconds (via {@link appointmentDisplayDurationDays}).
 * When counting shares' seconds encode the activity total but 5dp day conversion undershoots
 * (e.g. 3 × 0.33333 = 0.99999), bump the last counting share so days sum to that total.
 * Non-counting auditors are left unchanged.
 */
export const getReconciledAppointmentDisplayDurationDays = (
  appointments: AppointmentDTO[],
  activityDurationDays: number,
): Map<number, number> => {
  const byId = new Map<number, number>();
  appointments.forEach((appointment) => {
    byId.set(Number(appointment.id), appointmentDisplayDurationDays(appointment));
  });

  // Only counting roles participate in activity-duration reconcile. Never fall back to
  // including non-counting rows — that bumps their default full-day share (e.g. 1.00001).
  const counting = appointments.filter(isCountingWorkAppointment);
  if (counting.length < 2) {
    return byId;
  }

  // Stable order so the last chronological countable row absorbs the remainder.
  const orderedCounting = [...counting].sort(
    (a, b) =>
      new Date(a.from).getTime() - new Date(b.from).getTime() || Number(a.id) - Number(b.id),
  );

  const displayDays = orderedCounting.map((appointment) =>
    appointmentDisplayDurationDays(appointment),
  );
  const displaySum = roundDuration(displayDays.reduce((sum, days) => sum + days, 0));

  const secondsTarget = resolveSecondsEncodedActivityDays(orderedCounting);

  let target = roundDuration(activityDurationDays);
  if (target > 0) {
    const nearestActivityInt = Math.round(target);
    if (
      nearestActivityInt > 0 &&
      roundDuration(Math.abs(nearestActivityInt - target)) <= MIN_DURATION_DAYS
    ) {
      target = nearestActivityInt;
    }
  }

  // Prefer a clean total encoded by counting durationSeconds when activityDays is missing
  // or does not match the counting seconds sum.
  if (secondsTarget != null && secondsTarget > 0) {
    const secondsTotal = orderedCounting.reduce(
      (sum, appointment) => sum + Math.round(Number(appointment.durationSeconds)),
      0,
    );
    if (!(target > 0) || durationDaysToSeconds(target) !== secondsTotal) {
      target = secondsTarget;
    }
  } else if (!(target > 0)) {
    const nearestInt = Math.round(displaySum);
    if (
      nearestInt > 0 &&
      roundDuration(Math.abs(nearestInt - displaySum)) <= DISPLAY_DURATION_RECONCILE_TOLERANCE_DAYS
    ) {
      target = nearestInt;
    }
  }

  if (!(target > 0) || displaySum === target) {
    return byId;
  }

  const displayDrift = roundDuration(Math.abs(target - displaySum));
  const secondsMatch = secondsTarget === target;
  if (displayDrift > DISPLAY_DURATION_RECONCILE_TOLERANCE_DAYS && !secondsMatch) {
    return byId;
  }

  const reconciled = reconcileDisplayDurationsToTotal(displayDays, target);
  orderedCounting.forEach((appointment, index) => {
    byId.set(
      Number(appointment.id),
      reconciled[index] ?? byId.get(Number(appointment.id)) ?? 0,
    );
  });

  return byId;
};

const sumAppointmentDisplayDurationDays = (appointments: AppointmentDTO[]): number =>
  roundDuration(appointments.reduce((sum, a) => sum + appointmentDisplayDurationDays(a), 0));

/**
 * Group calendar duration for VisitCard ("Xd of Yd"). Uses per-appointment display days
 * (durationSeconds first), and if every slot has seconds, prefers that sum when reconciled
 * durationDays look inflated (incomplete activity fetch after inspector/date filters).
 * Tiny 5dp round-trip drift is ignored.
 */
export const sumAppointmentGroupDurationDays = (appointments: AppointmentDTO[]): number => {
  const fromDays = sumAppointmentDisplayDurationDays(appointments);
  const hasAllSeconds = appointments.every(
    (a) => a.durationSeconds != null && Number.isFinite(Number(a.durationSeconds)),
  );
  const fromSeconds = hasAllSeconds
    ? roundDuration(
        appointments.reduce(
          (sum, a) => sum + durationSecondsToAllocationDays(Number(a.durationSeconds)),
          0,
        ),
      )
    : 0;
  if (fromSeconds > 0 && roundDuration(fromDays - fromSeconds) > 0.0001) {
    return fromSeconds;
  }
  return fromDays;
};

export type AppointmentGroup = AppointmentDTO & {
  children: AppointmentDTO[];
  inspectorsCount: number;
  bufferBefore?: number;
  bufferAfter?: number;
  totalDurationDays?: number;
  travelDurationDays?: number;
  timezone?: TimezoneDTO;
  adjacentGroups?: AppointmentGroup[];
  totalDurationSeconds?: number;
};

/**
 * Auditable duration for the split-appointments modal: scheduled time minus travel (blocker) segments.
 * If that net is zero (e.g. legacy miscount of blocker travel), sums non-blocker segment lengths instead.
 */
export const splitModalTotalDurationDays = (group: AppointmentGroup): number => {
  const net = roundDuration((group.totalDurationDays ?? 0) - (group.travelDurationDays ?? 0));
  if (net > 0) {
    return net;
  }
  const segments = group.children?.length ? group.children : [group];
  return sumAppointmentDisplayDurationDays(segments.filter((a) => a.type !== 'blocker'));
};

export const mergeConsecutiveAppointments = (appointments: AppointmentDTO[]) => {
  const activityInspectorCount = new Map<string, number>();
  const groupedAppointmentsByActivity: { [key: string]: AppointmentDTO[] } = appointments.reduce(
    (acc: { [key: string]: AppointmentDTO[] }, appointment) => {
      if (!appointment.activity?.id || !appointment.inspector?.id) {
        return acc;
      }
      const activityId = appointment.activity.id.toString();
      const inspectorId = appointment.inspector.id.toString();
      if (!acc[`${activityId}-${inspectorId}`]) {
        activityInspectorCount.set(
          `${activityId}`,
          (activityInspectorCount.get(`${activityId}`) || 0) + 1
        );
      }
      acc[`${activityId}-${inspectorId}`] = acc[`${activityId}-${inspectorId}`] || [];
      acc[`${activityId}-${inspectorId}`].push(appointment);
      return acc;
    },
    {}
  );
  const result: AppointmentGroup[] = [];
  Object.values(groupedAppointmentsByActivity).forEach((appts) => {
    appts.sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());

    result.push({
      ...appts[0],
      children: [appts[0]],
      inspectorsCount: activityInspectorCount.get(`${appts[0].activity?.id}`) || 0,
      feedbackCodes:
        appts[0].feedbackCodes?.map((f) => ({ ...f, appointment: appts[0].from })) || [],
      bufferBefore: appts[0].type === 'blocker' ? 24 * 60 : 0,
      totalDurationDays: 1,
      totalDurationSeconds: appts[0].durationSeconds ?? 0,
    });

    const uniqueFeedbackCodes = new Set<string>(
      result[result.length - 1].feedbackCodes?.map((f) => f.code) || []
    );

    appts.forEach((appt, index) => {
      if (index === 0) return;
      const current = result[result.length - 1];

      const currentEndDate = new Date(current.to);
      const nextStartDate = new Date(appt.from);

      if ((nextStartDate.getTime() - currentEndDate.getTime()) / (1000 * 3600 * 24) > 1) {
        result.push({
          ...appt,
          children: [appt],
          inspectorsCount: activityInspectorCount.get(`${appt.activity?.id}`) || 0,
          totalDurationDays: 1,
          bufferBefore: appt.type === 'blocker' ? 24 * 60 : 0,
          totalDurationSeconds: appt.durationSeconds ?? 0,
        });
      } else {
        result[result.length - 1].to = appt.to;
        result[result.length - 1].children.push(appt);
        result[result.length - 1].totalDurationDays =
          (result[result.length - 1].totalDurationDays ?? 0) + 1;
        const newFeedback =
          appt.feedbackCodes
            ?.map((f) => ({ ...f, appointment: appt.from }))
            .filter((f) => !uniqueFeedbackCodes.has(f.code)) || [];
        newFeedback.map((f) => f.code).forEach((code) => uniqueFeedbackCodes.add(code));
        result[result.length - 1].feedbackCodes?.push(...newFeedback);
        if (appt.type === 'blocker') {
          result[result.length - 1].bufferAfter = 24 * 60;
        }
      }
    });
  });

  return result;
};

export const mergeConsecutiveAppointmentsByNearbyRelations = (appointments: AppointmentDTO[]) => {
  const findAppointmentById = (appts: AppointmentDTO[], id?: number | null) =>
    id != null ? appts.find((appointment) => appointment.id === id) : undefined;

  const isGroupLeader = (appointment: AppointmentDTO, appts: AppointmentDTO[]) => {
    const prevNearby = findAppointmentById(appts, appointment.prevNearbyAppointment?.id);
    const nearbyUnlinked =
      appointment.prevNearbyAppointment == null ||
      !prevNearby ||
      Math.abs(dayjs(prevNearby.to).diff(dayjs(appointment.from), 'days', true)) > 1;

    const prevSplit = findAppointmentById(appts, appointment.prevNearbySplitAppointment?.id);
    const splitUnlinked =
      appointment.prevNearbySplitAppointment == null ||
      !prevSplit ||
      Math.abs(dayjs(prevSplit.to).diff(dayjs(appointment.from), 'days', true)) > 1;

    return nearbyUnlinked && splitUnlinked;
  };

  const findNextChainedAppointment = (
    appts: AppointmentDTO[],
    currentId: number,
    groupLeader: AppointmentGroup,
  ) =>
    appts.find(
      (appointment) =>
        appointment.activity?.id === groupLeader.activity?.id &&
        Math.abs(dayjs(groupLeader.to).diff(dayjs(appointment.from), 'days', true)) <= 1 &&
        (appointment.prevNearbyAppointment?.id === currentId ||
          appointment.prevNearbySplitAppointment?.id === currentId),
    );

  const collectSplitChainIds = (seedId: number, appts: AppointmentDTO[]): Set<number> => {
    const ids = new Set<number>();
    const visit = (id: number) => {
      if (ids.has(id)) {
        return;
      }
      const appointment = appts.find((candidate) => candidate.id === id);
      if (!appointment) {
        return;
      }
      ids.add(id);
      if (appointment.prevNearbySplitAppointment?.id != null) {
        visit(appointment.prevNearbySplitAppointment.id);
      }
      if (appointment.nextNearbySplitAppointment?.id != null) {
        visit(appointment.nextNearbySplitAppointment.id);
      }
      appts.forEach((candidate) => {
        if (candidate.prevNearbySplitAppointment?.id === id) {
          visit(candidate.id);
        }
      });
    };
    visit(seedId);
    return ids;
  };

  const activityInspectorCount = new Map<string, number>();
  const groupedAppointmentsByActivity: { [key: string]: AppointmentDTO[] } = appointments.reduce(
    (acc: { [key: string]: AppointmentDTO[] }, appointment) => {
      if (!appointment.activity?.id || !appointment.inspector?.id) {
        return acc;
      }
      const activityId = appointment.activity.id.toString();
      const inspectorId = appointment.inspector.id.toString();
      if (!acc[`${activityId}-${inspectorId}`]) {
        activityInspectorCount.set(
          `${activityId}`,
          (activityInspectorCount.get(`${activityId}`) || 0) + 1
        );
      }
      acc[`${activityId}-${inspectorId}`] = acc[`${activityId}-${inspectorId}`] || [];
      acc[`${activityId}-${inspectorId}`].push(appointment);
      return acc;
    },
    {}
  );
  const groups: AppointmentGroup[] = [];

  Object.values(groupedAppointmentsByActivity).forEach((appts) => {
    appts.sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());
    const groupLeaderAppointments = appts.filter((appointment) =>
      isGroupLeader(appointment, appts),
    );
    groupLeaderAppointments.forEach((leader) => {
      groups.push({
        ...leader,
        children: [leader],
        inspectorsCount: activityInspectorCount.get(`${leader.activity?.id}`) || 0,
        feedbackCodes: leader.feedbackCodes?.map((f) => ({ ...f, appointment: leader.from })) || [],
        bufferBefore: leader.type === 'blocker' ? 24 * 60 : 0,
        travelDurationDays: leader.type === 'blocker' ? appointmentDisplayDurationDays(leader) : 0,
        totalDurationDays: sumAppointmentGroupDurationDays([leader]),
        timezoneId: leader.timezoneId,
        totalDurationSeconds: leader.durationSeconds ?? 0,
      });
    });

    const uniqueFeedbackCodes = new Set<string>();
    groups.forEach((groupLeader) => {
      uniqueFeedbackCodes.clear();
      groupLeader.feedbackCodes?.forEach((f) => uniqueFeedbackCodes.add(f.code));
      let endReached = false;
      let currentId = groupLeader.id;
      while (!endReached) {
        const currentAppointment = findNextChainedAppointment(appts, currentId, groupLeader);

        if (currentAppointment) {
          groupLeader.children.push(currentAppointment);
          currentId = currentAppointment.id;
          const newFeedback = currentAppointment.feedbackCodes
            ?.map((f) => ({ ...f, appointment: currentAppointment.from }))
            .filter((f) => !uniqueFeedbackCodes.has(f.code));
          newFeedback?.map((f) => f.code).forEach((code) => uniqueFeedbackCodes.add(code));
          groupLeader.feedbackCodes?.push(...newFeedback ?? []);
          groupLeader.totalDurationSeconds =
            (groupLeader.totalDurationSeconds ?? 0) +
            (currentAppointment.durationSeconds ?? 0);
          groupLeader.totalDurationDays = sumAppointmentGroupDurationDays(groupLeader.children);
          if (currentAppointment.type === 'blocker') {
            groupLeader.bufferAfter = 24 * 60;
            groupLeader.travelDurationDays = sumAppointmentGroupDurationDays(
              groupLeader.children.filter((a) => a.type === 'blocker'),
            );
          }
          groupLeader.to = currentAppointment.to;
          groupLeader.feedbackStatus =
            currentAppointment.feedbackStatus === AppointmentFeedbackStatus.PENDING
              ? AppointmentFeedbackStatus.PENDING
              : groupLeader.feedbackStatus;
        } else {
          endReached = true;
        }
      }
    });
  });
  //combinable groups, groups that belong to same inspector and activity, and they have one day or less gap between them
  groups.forEach((group) => {
    const splitChainIds = collectSplitChainIds(group.id, appointments);
    const adjacentGroups = groups.filter((candidate) => {
      if (candidate.id === group.id) {
        return false;
      }
      if (candidate.activity?.id !== group.activity?.id) {
        return false;
      }
      if (candidate.inspector?.id !== group.inspector?.id) {
        return false;
      }
      if (splitChainIds.has(candidate.id)) {
        return true;
      }
      return doDateRangesOverlap(group.from, group.to, candidate.from, candidate.to);
    });
    group.adjacentGroups = adjacentGroups ?? [];
    group.totalDurationDays = sumAppointmentGroupDurationDays(group.children);
    group.travelDurationDays = sumAppointmentGroupDurationDays(
      group.children.filter((a) => a.type === 'blocker'),
    );
  });
  return groups;
};



/**
 * Returns true if two date ranges overlap or are within one day of each other.
 * Comparison is done in days using dayjs.
 */
export const doDateRangesOverlap = (
  startA: dayjs.ConfigType,
  endA: dayjs.ConfigType,
  startB: dayjs.ConfigType,
  endB: dayjs.ConfigType
): boolean => {
  const a0 = dayjs(startA);
  const a1 = dayjs(endA);
  const b0 = dayjs(startB);
  const b1 = dayjs(endB);

  // Gap = how far apart the two ranges are (negative means they overlap)
  // If A ends before B starts → gap = b0 - a1
  // If B ends before A starts → gap = a0 - b1
  // We take the larger gap; if both are ≤ 1 day, ranges are "close enough"
  const gapDays = Math.max(b0.diff(a1, 'day', true), a0.diff(b1, 'day', true));

  return gapDays <= 1;
};

export interface UnavailabilityPeriodGroup {
  type: 'EXTERNAL_CALENDAR_EVENT';
  from: string;
  to: string;
  inspector: InspectorDTO;
  children: { from: string; to: string }[];
  editable: boolean;
}

export const mergeConsecutiveUnavailabilityPeriods = (
  unavailabilityPeriods: UnavailabilityPeriodDTO[]
): UnavailabilityPeriodGroup[] => {
  const sortedUnavailabilityPeriods = unavailabilityPeriods.sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );

  const unavailabilityPeriodsByInspector = new Map<number, UnavailabilityPeriodDTO[]>();
  sortedUnavailabilityPeriods.forEach((e) => {
    if (e.inspector) {
      unavailabilityPeriodsByInspector.set(e.inspector.id, [
        ...(unavailabilityPeriodsByInspector.get(e.inspector.id) || []),
        e,
      ]);
    }
  });
  const groupedUnavailabilityPeriods: UnavailabilityPeriodGroup[] = [];
  Array.from(unavailabilityPeriodsByInspector.values()).forEach((periods) => {
    periods.forEach((period, index) => {
      if (index === 0) {
        groupedUnavailabilityPeriods.push({
          type: 'EXTERNAL_CALENDAR_EVENT',
          from: period.startDate,
          to: formatEndDate(period.endDate),
          inspector: period.inspector as InspectorDTO,
          children: [
            {
              from: period.startDate,
              to: formatEndDate(period.endDate),
            },
          ],
          editable: false,
        });
      } else {
        const lastPeriod = groupedUnavailabilityPeriods[groupedUnavailabilityPeriods.length - 1];
        if (
          dayjs(lastPeriod.from).isSameOrBefore(dayjs(period.startDate), 'day') &&
          (dayjs(lastPeriod.to).isSame(dayjs(period.startDate), 'day') ||
            dayjs(lastPeriod.to).isAfter(dayjs(period.startDate), 'day'))
        ) {
          if (dayjs(period.endDate).isAfter(lastPeriod.to)) {
            lastPeriod.to = formatEndDate(period.endDate);
          }
          lastPeriod.children.push({
            from: period.startDate,
            to: formatEndDate(period.endDate),
          });
        } else {
          groupedUnavailabilityPeriods.push({
            type: 'EXTERNAL_CALENDAR_EVENT',
            from: period.startDate,
            to: formatEndDate(period.endDate),
            inspector: period.inspector as InspectorDTO,
            children: [
              {
                from: period.startDate,
                to: formatEndDate(period.endDate),
              },
            ],
            editable: false,
          });
        }
      }
    });
  });
  return groupedUnavailabilityPeriods;
};

const formatEndDate = (endDate: string) => {
  const utcDate = dayjs.utc(endDate);

  if (utcDate.format('HH:mm') === '00:00') {
    const loggedInUserTimezone = dayjs.tz.guess();
    // Get the timezone offset in hours for the user's timezone
    const timezoneOffset = dayjs.tz(utcDate, loggedInUserTimezone).utcOffset() / 60;
    // Subtract the timezone offset to make it midnight in user's timezone
    const adjustedDate = utcDate.subtract(timezoneOffset, 'hours').subtract(1, 'minute');
    const result = adjustedDate.format();
    return result;
  }

  return endDate;
};